/**
 * Runtime Analytics Module
 *
 * Динамическое подключение аналитических систем на основе данных от backend.
 * Использует архитектуру обработчиков (handler map) для лёгкого расширения.
 *
 * Поддерживаемые провайдеры:
 *   - MAIL_RU_COUNTER
 *   - VK_PIXEL
 *
 * Добавление нового провайдера:
 *   1. Создать функцию-обработчик (пример: initGoogleAnalytics)
 *   2. Добавить её в объект handlers по ключу провайдера
 *   3. Никакой другой код менять не нужно
 *
 * Все провайдеры автоматически подчиняются механизму согласия
 * через analyticsConsent (проверка в App.vue перед вызовом initRuntimeAnalytics).
 */

// Карта обработчиков: provider → функция инициализации
const handlers = {
  MAIL_RU_COUNTER: initMailRuCounter,
  VK_PIXEL: initVkPixel,
  // YANDEX_METRIKA: initYandexMetrika, // будет добавлен в будущем
  // GOOGLE_ANALYTICS: initGoogleAnalytics, // будет добавлен в будущем
}

// Множество уже инициализированных связок provider+identifier
// Ключ: `${provider}_${identifier}`
const initializedProviders = new Set()

/**
 * Инициализировать runtime-аналитику.
 *
 * @param {Array<{provider: string, identifier: string}>} analytics
 *   Массив объектов с провайдерами и идентификаторами от backend.
 *
 * Пример:
 *   initRuntimeAnalytics([
 *     { provider: 'MAIL_RU_COUNTER', identifier: '3780867' }
 *   ])
 */
export function initRuntimeAnalytics(analytics) {
  if (!Array.isArray(analytics)) {
    return
  }

  analytics.forEach((item) => {
    if (!item || !item.provider || !item.identifier) {
      return
    }

    const handler = handlers[item.provider]
    if (!handler) {
      return
    }

    const key = `${item.provider}_${item.identifier}`
    if (initializedProviders.has(key)) {
      return
    }

    handler(item.identifier)
    initializedProviders.add(key)
  })
}

/**
 * Обработчик для Mail.ru Counter.
 *
 * Динамически загружает официальный скрипт Mail.ru Counter
 * и добавляет noscript-пиксель для fallback.
 *
 * @param {string} identifier — идентификатор счётчика (например, "3780867")
 */
function initMailRuCounter(identifier) {
  // Если _tmr уже существует — счётчик уже был инициализирован
  if (window._tmr) {
    return
  }

  // Инициализация очереди _tmr
  window._tmr = window._tmr || []
  window._tmr.push({ id: identifier, type: 'pageView', start: new Date().getTime() })

  // Создание script-тега
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://top-fwz1.mail.ru/js/code.js'

  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  }

  // Добавление noscript-пикселя для случаев, когда JS отключён
  const noscript = document.createElement('noscript')
  const img = document.createElement('img')
  img.src = `https://top-fwz1.mail.ru/counter?id=${identifier};js=na`
  img.style = 'border:0;position:absolute;left:-9999px;'
  img.alt = 'Mail.ru'
  noscript.appendChild(img)
  document.body.appendChild(noscript)
}

/**
 * Обработчик для VK Pixel.
 *
 * Динамически загружает VK Open API SDK и инициализирует пиксель.
 * До вызова этой функции пиксель не загружается и не отправляет события.
 *
 * @param {string} identifier — идентификатор пикселя (например, "VK-RTRG-1711952-b4a60")
 */
function initVkPixel(identifier) {
  if (!identifier) {
    return
  }

  // Если VK SDK уже загружен и пиксель инициализирован — не дублируем
  if (window.VK && window.VK.Retargeting) {
    return
  }

  // Создание script-тега для загрузки VK Open API
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.async = true
  script.src = 'https://vk.com/js/api/openapi.js?169'
  script.onload = function () {
    // eslint-disable-next-line no-undef
    VK.Retargeting.Init(identifier)
    // eslint-disable-next-line no-undef
    VK.Retargeting.Hit()
  }
  document.head.appendChild(script)

  // Noscript fallback для случаев, когда JS отключён
  const noscript = document.createElement('noscript')
  const img = document.createElement('img')
  img.src = `https://vk.com/rtrg?p=${identifier}`
  img.style = 'position:fixed; left:-999px;'
  img.alt = 'VK Pixel'
  noscript.appendChild(img)
  document.body.appendChild(noscript)
}

/**
 * Сбросить состояние инициализированных провайдеров.
 * Используется только для тестирования.
 */
export function __resetInitializedProviders() {
  initializedProviders.clear()
}
